import React, { ChangeEvent, useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@material-ui/core";
import { useStyles } from "../personalInformation.styles";
import {
  Errors,
  IPersonalInformationInstitutionalFields,
  Types,
} from "../../../../shared/interfaces/iform";
import { isFormInputValid } from "../../../../shared/utils/formUtils";
import { FormTypes } from "../../../../shared/interfaces/formTypes.enum";
import {
  PERSONAL_INFORMATION,
  REQUIRED_FIELD_MISSING,
} from "../../../../shared/utils/textConstants";
import { EntityTypes } from "../../../../shared/interfaces/EntityTypes.enum";
import { MAX_ZIPCODE_LENGTH, US_STATES } from "../../../../shared/utils/valueConstants";

const errorsDefault: Errors = {
  entityName: false,
  taxId: false,
  entityType: false,
  phoneNumber: false,
  entityEmail: false,
  address: false,
  firstName: false,
  lastName: false,
  email: false,
  zipCode: false,
  city: false,
  state: false,
};

const types: Types = {
  entityName: FormTypes.Text,
  taxId: FormTypes.TaxId,
  entityType: FormTypes.EntityType,
  entityCode: FormTypes.EntityCode,
  phoneNumber: FormTypes.Phone,
  entityEmail: FormTypes.EntityEmail,
  address: FormTypes.Address,
  firstName: FormTypes.Text,
  lastName: FormTypes.Text,
  email: FormTypes.Email,
  zipCode: FormTypes.ZipCode,
  city: FormTypes.Text,
  state: FormTypes.Text,
};

interface IProps {
  id: string;
  formFields: IPersonalInformationInstitutionalFields;
  handleSetStepCanAdvance: (id: string, flag: boolean) => void;
  handleValueChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type?: FormTypes,
  ) => void;
  handleFieldChange: (label: string, value: string) => void;
}

const InstitutionalAccount = (props: IProps) => {
  const {
    id,
    formFields,
    handleSetStepCanAdvance,
    handleValueChange,
    handleFieldChange,
  } = props;
  const [errors, setErrors] = useState(errorsDefault);
  const classes = useStyles();

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    const label = e.target.name;
    const value = e.target.value;

    setErrors((prevErrors) => {
      return {
        ...prevErrors,
        [label]: !isFormInputValid(types[label], value),
      };
    });
    validateNextStep();
  };

  const validateNextStep = () => {
    const isFormValid =
      Object.entries(types).filter(
        ([key, value]) => !isFormInputValid(value, formFields[key]),
      ).length === 0;
    if (isFormValid) {
      handleSetStepCanAdvance(id, true);
    } else {
      handleSetStepCanAdvance(id, false);
    }
  };

  const handleRadioButton = (e: ChangeEvent<HTMLInputElement>) => {
    handleValueChange(e, types[e.target.value]);

    validateNextStep()
  };

  const handleOtherInput = (e: ChangeEvent<HTMLInputElement>) => {
    handleValueChange(e, types[e.target.value]);

    validateNextStep()
  };

  const onSelect = (e: ChangeEvent<{ value: any; name?: any }>) => {
    handleFieldChange(e.target.name, e.target.value);
  };

  return (
    <FormControl className={classes.root} component="fieldset">
      <TextField
        id="entityName"
        name="entityName"
        label="Entity Name"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.entityName}
        value={formFields.entityName}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.entityName && REQUIRED_FIELD_MISSING}
      />
      <TextField
        id="taxId"
        name="taxId"
        label="Tax ID"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.taxId}
        value={formFields.taxId}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
      <fieldset className={classes.radioGroup}>
        <legend className={classes.radioGroupTitle}>Entity Type &nbsp;*</legend>
        <RadioGroup aria-label="entityType" id="entityCode" name="entityCode">
          {(Object.keys(EntityTypes) as Array<keyof typeof EntityTypes>)
            .filter(
              (entityType) => EntityTypes[entityType] !== EntityTypes.OTHER,
            )
            .map((entityType) => (
              <FormControlLabel
                value={entityType}
                className={classes.radioButton}
                key={entityType}
                control={
                  <Radio
                    color="primary"
                    checked={formFields.entityCode === entityType}
                    onChange={handleRadioButton}
                  />
                }
                label={EntityTypes[entityType]}
              />
            ))}
          <div className={classes.otherContainer}>
            <FormControlLabel
              value={EntityTypes.OTHER}
              className={classes.radioButton}
              control={
                <Radio
                  color="primary"
                  checked={formFields.entityCode === EntityTypes.OTHER}
                  onChange={handleRadioButton}
                />
              }
              label={EntityTypes.OTHER}
            />
            <TextField
              id="entityType"
              name="entityType"
              placeholder="Type here"
              helperText={errors.entityType && "Required"}
              required={formFields.entityCode === EntityTypes.OTHER}
              value={formFields.entityCode === EntityTypes.OTHER ? formFields.entityType : ""}
              className={classes.input}
              inputProps={{ "aria-label": "other" }}
              disabled={formFields.entityCode !== EntityTypes.OTHER}
              error={errors.entityType}
              onChange={handleOtherInput}
              onBlur={handleOnBlur}
            />
          </div>
        </RadioGroup>
      </fieldset>
      <TextField
        id="phoneNumber"
        name="phoneNumber"
        label="Phone Number"
        variant="outlined"
        style={{ width: "11.75rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        inputProps={{ maxLength: 15 }}
        error={errors.phoneNumber}
        value={formFields.phoneNumber}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
      <TextField
        id="entityEmail"
        name="entityEmail"
        label="Entity Email"
        variant="outlined"
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.entityEmail}
        value={formFields.entityEmail}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
      <TextField
        id="address"
        name="address"
        label="Address"
        variant="outlined"
        style={{ width: "20rem" }}
        inputProps={{ maxLength: 250 }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        required
        size="small"
        error={errors.address}
        value={formFields.address}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />

      <TextField
        id="city"
        name="city"
        label="City"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        inputProps={{ maxLength: 250 }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.city}
        value={formFields.city}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.city && REQUIRED_FIELD_MISSING}
      />

      <FormControl error={!!errors.state}>
        <Select
          id="state"
          name="state"
          variant="outlined"
          required
          style={{
            width: "20rem",
            height: "2.75rem",
            marginBottom: errors.state ? 0 : "20px",
          }}
          className={classes.textField}
          error={errors.state}
          value={formFields.state || ""}
          onBlur={handleOnBlur}
          onChange={(e) => onSelect(e)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            <span
              className={errors.state ? classes.error : classes.placeholder}
            >
              State *
            </span>
          </MenuItem>
          {US_STATES.map((state) => (
            <MenuItem value={state.code} key={state.code}>
              {state.name}
            </MenuItem>
          ))}
        </Select>
        {errors.state && (
          <FormHelperText>{REQUIRED_FIELD_MISSING}</FormHelperText>
        )}
      </FormControl>

      <TextField
        id="zipCode"
        name="zipCode"
        label="Zip Code"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        inputProps={{ maxLength: MAX_ZIPCODE_LENGTH }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.zipCode}
        value={formFields.zipCode}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />

      <div className={classes.personalInformationTitle}>
        {PERSONAL_INFORMATION}
      </div>
      <br />
      <TextField
        id="firstName"
        name="firstName"
        label="First Name"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.firstName}
        value={formFields.firstName}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.firstName && REQUIRED_FIELD_MISSING}
      />
      <TextField
        id="lastName"
        name="lastName"
        label="Last Name"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.lastName}
        value={formFields.lastName}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
        helperText={errors.lastName && REQUIRED_FIELD_MISSING}
      />
      <TextField
        id="email"
        name="email"
        label="Email"
        variant="outlined"
        required
        style={{ width: "20rem" }}
        className={classes.textField}
        InputLabelProps={{
          className: classes.textFieldInput,
        }}
        size="small"
        error={errors.email}
        value={formFields.email}
        onChange={(e) => handleValueChange(e, types[e.target.name])}
        onBlur={handleOnBlur}
      />
    </FormControl>
  );
};

export default InstitutionalAccount;
